// Chat sheet — slides up from the bottom; one tab per accessible channel.
//
// Channels the user can see are decided server-side: lobby/alive players see
// 'public', mafia faction sees 'mafia', dead players see 'dead'. We surface
// a tab only if the corresponding messages array is present in the chat map —
// the absence of a key already means "no access".
//
// This component serves TWO callers:
//   - the player path (App passes `view` + a posting composer), and
//   - the host moderation path (App passes `chat={hostView.chat}` + `canPost=
//     false`, no `view`), which shows every channel read-only.
// `view` is therefore optional; channels are derived from the `chat` override
// when no view is present, and own-message styling falls back to `meId`.

import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  ChatChannel,
  ChatMessage,
  ClientMsg,
  Lang,
  PlayerView,
} from '../../../shared/src/types.js';
import { LIMITS } from '../../../shared/src/config.js';
import { useFocusTrap } from '../useFocusTrap.js';

interface Props {
  t: Lang;
  send: (msg: ClientMsg) => void;
  onClose: () => void;
  /** Player projection. Optional so the host can use this read-only. */
  view?: PlayerView;
  /** Channel map override (defaults to `view?.chat`). Host passes the full
   *  HostView.chat record. */
  chat?: Partial<Record<ChatChannel, ChatMessage[]>>;
  /** Whether the composer is shown. Host panel passes false. */
  canPost?: boolean;
  /** Seat id of the viewer, for own-message styling when there's no `view`. */
  meId?: string;
  /** Fired with the channel the user is currently reading, so the parent can
   *  mark only that channel seen (per-channel unread). */
  onSeenChannel?: (ch: ChatChannel) => void;
}

const CHANNEL_LABEL: Record<ChatChannel, Record<Lang, string>> = {
  public: { ru: 'ОБЩИЙ', uk: 'ЗАГАЛЬНИЙ' },
  mafia: { ru: 'МАФИЯ', uk: 'МАФІЯ' },
  dead: { ru: 'МЁРТВЫЕ', uk: 'МЕРТВІ' },
};

const CHANNEL_ACCENT: Record<ChatChannel, string> = {
  public: 'var(--bone)',
  mafia: 'var(--poison-red)',
  dead: 'var(--smoke)',
};

export function Chat({
  t,
  send,
  onClose,
  view,
  chat,
  canPost = true,
  meId,
  onSeenChannel,
}: Props) {
  // Resolve the channel map: explicit override wins, else the player view.
  const chatMap = chat ?? view?.chat;
  const myId = meId ?? view?.me.id;

  // Available channels — keys present in the resolved chat map.
  const available = useMemo<ChatChannel[]>(() => {
    const out: ChatChannel[] = [];
    if (chatMap?.public) out.push('public');
    if (chatMap?.mafia) out.push('mafia');
    if (chatMap?.dead) out.push('dead');
    return out;
  }, [chatMap]);

  const [active, setActive] = useState<ChatChannel>(available[0] ?? 'public');
  const [draft, setDraft] = useState('');
  const [cooling, setCooling] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  // Mirror the server's per-player chat throttle locally so a too-fast second
  // send is held back (keeping the draft) instead of being silently dropped
  // server-side with the input already cleared.
  const lastSentRef = useRef(0);
  const coolTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // If the active channel becomes unavailable (player just died etc.), reset
  // to the first one we still have.
  useEffect(() => {
    if (!available.includes(active) && available.length > 0) setActive(available[0]);
  }, [available, active]);

  const messages: ChatMessage[] = chatMap?.[active] ?? [];

  // Tell the parent which channel is being read so it can mark only that
  // channel seen. Re-fires when the active channel changes or new messages
  // land in the open channel.
  useEffect(() => {
    if (available.includes(active)) onSeenChannel?.(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, messages.length]);

  // Auto-scroll to bottom when new messages arrive or channel switches.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, active]);

  // Clean up the cooling timer on unmount.
  useEffect(
    () => () => {
      if (coolTimerRef.current) clearTimeout(coolTimerRef.current);
    },
    [],
  );

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    // Respect the server's chat spacing: if we'd be rejected, keep the draft
    // and flash the send button so the throttle is visible, not silent.
    const now = Date.now();
    if (now - lastSentRef.current < LIMITS.chatRateMs) {
      setCooling(true);
      if (coolTimerRef.current) clearTimeout(coolTimerRef.current);
      coolTimerRef.current = setTimeout(() => setCooling(false), LIMITS.chatRateMs);
      return;
    }
    lastSentRef.current = now;
    send({ kind: 'chat:send', channel: active, text });
    setDraft('');
  };

  // Arrow-key roving across the tablist (left/right wrap).
  const onTabKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const next = available[(idx + delta + available.length) % available.length];
    if (next) setActive(next);
  };

  const tabId = (ch: ChatChannel) => `chat-tab-${ch}`;
  const panelId = (ch: ChatChannel) => `chat-panel-${ch}`;

  // Modal semantics matching the Codex overlay (audit P2): trap focus inside
  // the sheet, close on Escape, restore focus to the opener on close.
  const dialogRef = useFocusTrap<HTMLDivElement>(true, onClose);

  return (
    <div
      className="chat-sheet"
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Чат"
      onClick={(e) => e.stopPropagation()}
    >
      <header className="chat-head">
        <div
          className="chat-tabs"
          role="tablist"
          aria-label={t === 'ru' ? 'Каналы чата' : 'Канали чату'}
        >
          {available.length === 0 ? (
            <div className="mono" style={{ color: 'var(--smoke)', padding: '8px 12px' }}>
              {t === 'ru' ? 'НЕТ ДОСТУПНЫХ КАНАЛОВ' : 'НЕМАЄ ДОСТУПНИХ КАНАЛІВ'}
            </div>
          ) : (
            available.map((ch, idx) => {
              const count = chatMap?.[ch]?.length ?? 0;
              const selected = active === ch;
              return (
                <button
                  key={ch}
                  id={tabId(ch)}
                  role="tab"
                  aria-selected={selected}
                  aria-controls={panelId(ch)}
                  aria-label={`${CHANNEL_LABEL[ch][t]} · ${count}`}
                  tabIndex={selected ? 0 : -1}
                  className={`chat-tab ${selected ? 'on' : ''}`}
                  onClick={() => setActive(ch)}
                  onKeyDown={(e) => onTabKeyDown(e, idx)}
                  style={{
                    borderColor: selected ? CHANNEL_ACCENT[ch] : 'var(--ink-600)',
                    color: selected ? CHANNEL_ACCENT[ch] : 'var(--bone-dim)',
                  }}
                >
                  <span className="sub">{CHANNEL_LABEL[ch][t]}</span>
                  <span className="mono chat-tab-count">{count}</span>
                </button>
              );
            })
          )}
        </div>
        <button
          className="codex-close"
          onClick={onClose}
          aria-label={t === 'ru' ? 'Закрыть' : 'Закрити'}
        >
          ✕
        </button>
      </header>

      <div
        className="chat-list"
        ref={listRef}
        role="tabpanel"
        id={available.includes(active) ? panelId(active) : undefined}
        aria-labelledby={available.includes(active) ? tabId(active) : undefined}
      >
        {messages.length === 0 && (
          <div className="chat-empty mono">
            {t === 'ru' ? 'НИКТО ЕЩЁ НЕ ПИСАЛ' : 'НІХТО ЩЕ НЕ ПИСАВ'}
          </div>
        )}
        {messages.map((m) => {
          const isMe = !!myId && m.senderId === myId;
          return (
            <div key={m.id} className={`chat-msg ${isMe ? 'mine' : ''}`}>
              <div className="chat-msg-meta mono">
                <span className="chat-msg-name" style={{ color: CHANNEL_ACCENT[active] }}>
                  {m.senderName}
                </span>
                <span className="chat-msg-time">
                  {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="chat-msg-text">{m.text}</div>
            </div>
          );
        })}
      </div>

      {canPost && available.includes(active) && view?.me.alive !== undefined && (
        <form
          className="chat-input"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, LIMITS.chatMax))}
            placeholder={t === 'ru' ? 'Сообщение…' : 'Повідомлення…'}
            maxLength={LIMITS.chatMax}
            className="mono"
          />
          <button
            type="submit"
            className={`btn btn-blood chat-send ${cooling ? 'cooling' : ''}`}
            disabled={!draft.trim() || cooling}
          >
            <span className="display">→</span>
          </button>
        </form>
      )}
    </div>
  );
}
